import React, { useEffect, useRef } from "react";
import { WebJourneySplashProps } from "./types";
import "./WebJourneySplash.css";
import { hotAirBallon } from "../../../../appAssets";
import { useNavigate } from "react-router-dom";
import { webRoutes } from "../../../../router/webRoutes";
import WebLottiePlayer from "../../../WebStoryScreen/Components/WebLottiePlayer";

const DEFAULT_DURATION_MS = 3000;

/**
 * Web port of `MyJourneyScreen/JourneySplash.tsx`.
 * Shows the hot-air-balloon splash, then navigates to My Journey.
 */
const WebJourneySplash: React.FC<WebJourneySplashProps> = ({
  updateState,
  durationMs = DEFAULT_DURATION_MS,
  imageSrc = hotAirBallon,
  className = "",
}) => {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Force the lottie-web SVG to cover the full container (like object-fit: cover).
  // lottie-web injects the SVG asynchronously, so we watch with MutationObserver.
  useEffect(() => {
    const container = wrapRef.current;
    if (!container) return;

    const applySlice = (svg: SVGSVGElement) => {
      svg.setAttribute("preserveAspectRatio", "xMidYMid slice");
      svg.style.width = "100%";
      svg.style.height = "100%";
    };

    const tryApply = () => {
      const svg = container.querySelector<SVGSVGElement>("svg");
      if (svg) applySlice(svg);
    };

    tryApply();
    const observer = new MutationObserver(tryApply);
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [imageSrc]);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(webRoutes.journey);
      updateState();
    }, durationMs);
    return () => clearTimeout(timer);
  }, [updateState, durationMs, navigate]);

  return (
    <div
      ref={wrapRef}
      className={`web-journey-splash ${className}`.trim()}
      role="dialog"
      aria-modal
      aria-label="Journey splash"
    >
      <WebLottiePlayer
        source={imageSrc ?? hotAirBallon}
        className="web-journey-splash__image"
        style={{ width: "100%", height: "100%", display: "block" }}
        autoPlay
        loop
      />
    </div>
  );
};

export default WebJourneySplash;
